import { DATE_FORMAT } from '@/helpers/dates';
import { getActiveBillingBlocks } from '@/helpers/getActiveBillingBlocks';
import usePermissions from '@/hooks/usePermissions';
import { Button } from '@palmetto/palmetto-components';
import { DateTime } from 'luxon';

import {
  currentPaymentMethodLabels,
  ExternalReferenceType,
  FinancialAccountPaymentMethodType,
  FinancingDataResponse,
  SavedPaymentMethodFullResponse,
  UserPermissions,
} from 'types';

import { useAccountDetailsContext } from './AccountDetailsContext';

interface UseSupportPropertiesParams {
  paymentMethod: SavedPaymentMethodFullResponse[];
  financingData: FinancingDataResponse | undefined;
}

export function useSupportProperties({ paymentMethod, financingData }: UseSupportPropertiesParams) {
  const { account, openModal } = useAccountDetailsContext();
  const userPermissions = usePermissions();
  const hasMeasurableGenerationViewer = userPermissions.includes(UserPermissions.measurableGenerationViewer);
  const concordReferenceId = account?.externalReferenceIds?.find(
    (ref: any) => ref.type === ExternalReferenceType.concord,
  )?.id;
  const hasConcordAccountBeenCreated = concordReferenceId;
  const hasAchOnFile = paymentMethod.some((m) => m.isAutoPayMethod && !!m.bankAccount);

  return {
    heading: 'Account Support',
    showInternalOnly: true,
    values: [
      { label: 'Payment Provider', value: account?.inboundPayments ? account.inboundPayments.provider : 'Stripe' },
      {
        label: 'Provider ID',
        value: concordReferenceId,
      },
      {
        label: 'Current Payment Plan',
        value:
          account?.firstPaymentDueDate &&
          financingData?.currentPaymentMethod &&
          currentPaymentMethodLabels[financingData.currentPaymentMethod]
            ? currentPaymentMethodLabels[financingData.currentPaymentMethod]
            : '---',
      },
      {
        label: hasAchOnFile ? 'View/Add Auto ACH Payment Method' : 'Add Auto ACH Payment Method',
        value: hasAchOnFile ? (
          <Button
            variant="primary"
            tone="neutral"
            size="xs"
            onClick={() => openModal('ach')}
            style={{ maxWidth: '240px', marginLeft: 'auto', display: 'block' }}
          >
            {FinancialAccountPaymentMethodType.bankAccount}
          </Button>
        ) : (
          <Button
            variant="primary"
            size="xs"
            onClick={() => openModal('ach')}
            style={{ maxWidth: '240px', marginLeft: 'auto', display: 'block' }}
          >
            Add
          </Button>
        ),
      },
      ...(hasConcordAccountBeenCreated
        ? [
            {
              label: 'One-Time Payment',
              value: (
                <Button
                  variant="primary"
                  tone="neutral"
                  size="xs"
                  onClick={() => openModal('oneTimePayment')}
                  style={{ maxWidth: '240px', marginLeft: 'auto', display: 'block' }}
                >
                  Make One-Time Payment
                </Button>
              ),
            },
          ]
        : []),
      {
        label: 'Operation At Date (UTC)',
        value: account?.operationAtData?.operationAtDate
          ? DateTime.fromISO(account.operationAtData.operationAtDate, { setZone: true })
              .toUTC()
              .toFormat(DATE_FORMAT)
          : '---',
      },
      {
        label: 'Placed In Service Date (UTC)',
        value: account?.placedInServiceData?.placedInServiceDate
          ? DateTime.fromISO(account.placedInServiceData.placedInServiceDate, { setZone: true })
              .toUTC()
              .toFormat(DATE_FORMAT)
          : '---',
      },
      ...(hasMeasurableGenerationViewer
        ? [
            {
              label: 'Sundial Measurable Generation (UTC)',
              value: account?.sundialFirstMeasurableGenerationDate
                ? DateTime.fromISO(account.sundialFirstMeasurableGenerationDate as unknown as string, {
                    setZone: true,
                  }).toFormat(DATE_FORMAT)
                : '---',
            },
          ]
        : []),
      {
        label: 'Est. First Payment Due Date',
        value: account?.firstPaymentDueDate ? account?.firstPaymentDueDate : '---',
      },
      {
        label: 'Billing Blockers',
        value: (() => {
          const activeBillingBlocks = getActiveBillingBlocks(account?.billingBlocks);
          return activeBillingBlocks.length > 0 ? (
            <Button variant="primary" tone="danger" size="xs" onClick={() => openModal('billingBlock')}>
              Blockers ({activeBillingBlocks.length})
            </Button>
          ) : (
            '---'
          );
        })(),
      },
    ],
  };
}
